import {
  fromEnumAsStringOrNull, toEnumFromStringOrNull,
} from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { cloneData, cloneDataOrNull, isSameData } from '../dto/data-model-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { PhotoReference } from './file-reference/photo-reference.js';
import { InvoiceVat } from './invoicing/invoice-vat.js';
import { LocalizedString } from './localized-string.js';
import { Price } from './price.js';

export class TicketSupplementTemplate extends DataModel {
  static readonly TYPE_ID: string = '40c5c0801626451d8742067c58b3eee7';

  private constructor(
    public readonly uniqueId: string,
    public readonly names: LocalizedString,
    public readonly price: Price,
    public readonly overrideVat: InvoiceVat | null,
    public readonly pdfBackground: PhotoReference | null,
  ) {
    super();
  }

  get $typeId(): string {
    return TicketSupplementTemplate.TYPE_ID;
  }

  static create(args: {
    uniqueId: string,
    names: LocalizedString,
    price: Price,
    overrideVat: InvoiceVat | null,
    pdfBackground: PhotoReference | null,
  }): TicketSupplementTemplate {
    return new TicketSupplementTemplate(
        args.uniqueId,
        args.names,
        args.price,
        args.overrideVat,
        args.pdfBackground,
    );
  }

  static fromData(
      context: SerializationContext,
      data: SerializedData,
  ): TicketSupplementTemplate {
    return this.create({
      uniqueId: MapUtils.getString(data, 'uniqueId'),
      names: context.fromData<LocalizedString>(MapUtils.getRecord(data, 'names')),
      price: Price.decode(MapUtils.getString(data, 'price')),
      overrideVat: toEnumFromStringOrNull(
          MapUtils.getStringOrNull(data, 'overrideVat'),
          InvoiceVat),
      pdfBackground: context.fromDataOrNull<PhotoReference>(
          MapUtils.getRecordOrNull(data, 'pdfBackground')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      names: context.toData(this.names),
      price: this.price.encode(),
      overrideVat: fromEnumAsStringOrNull(this.overrideVat),
      pdfBackground: context.toDataOrNull(this.pdfBackground),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    names = cloneData(this.names),
    price = cloneData(this.price),
    overrideVat = this.overrideVat,
    pdfBackground = cloneDataOrNull(this.pdfBackground),
  }: {
    uniqueId?: string,
    names?: LocalizedString,
    price?: Price,
    overrideVat?: InvoiceVat | null,
    pdfBackground?: PhotoReference | null,
  } = {}): this {
    return TicketSupplementTemplate.create({
      uniqueId,
      names,
      price,
      overrideVat,
      pdfBackground,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      isSameData(other.names, this.names) &&
      isSameData(other.price, this.price) &&
      other.overrideVat === this.overrideVat &&
      isSameData(other.pdfBackground, this.pdfBackground);
  }
}

export class TicketSupplementTemplateBuilder extends DataModelBuilder<TicketSupplementTemplate> {
  build(context: SerializationContext, map: SerializedData): TicketSupplementTemplate {
    return TicketSupplementTemplate.fromData(context, map);
  }
}
